/* eslint-disable no-unused-vars */
import { any, func, string, bool } from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { Checkbox, TextAreaGroup } from "..";
import { Header, DOB } from "../../components/BasicInfo";
import history from "../../history";
import HelpIcon from "../HelpIcon";
import OptionsButton from "../OptionsButton";
import OverflowedText from "../OverflowedText";
import SelectListGroup from "../SelectListGroup";
import TextFieldGroup from "../TextFieldGroup";
import * as styles from "./styles.module.scss";
import RadioButton from "../RadioButton";
import Table from "./table";
import Spinner from "../Spinner";

const FormCreator = ({
  formTitle,
  description,
  fields,
  setField,
  handleSubmit,
  handleCancel = () => history.push("/profile/edit/royal-mail-onboarding"),
  onboardingBranch = "royalMail",
  helper = "All fields marked with an asterisk* are required",
  customHtml,
  handleSave,
  isCustomSaveButton,
  disableSave,
  loading,
}) => {
  /* 
  Component creator to ease the pain of creating very similar forms
  Form is created based on initialState object you put in through props
  'fields', and 'setField' are state and change handler of useForm hook
  */

  const checkIfShouldShow = (dependOn, display, dependOnAny, dependOnExact) => {
    if (display === false) {
      return false;
    }

    if (dependOn) {
      return dependOn.every((dep) => {
        const { fieldKey, allowedValues } = dep;
        const actualValue = fields[fieldKey].value;

        if (allowedValues) {
          return typeof actualValue === "object"
            ? actualValue[allowedValues]
            : allowedValues && allowedValues.includes(actualValue);
        }

        return typeof actualValue === "object"
          ? actualValue[allowedValues]
          : actualValue === true ||
              (allowedValues && allowedValues.includes(actualValue));
      });
    }

    if (dependOnAny) {
      return dependOnAny.some((dep) => {
        const { fieldKey, allowedValues } = dep;
        const actualValue = fields[fieldKey].value;

        if (allowedValues) {
          return typeof actualValue === "object"
            ? actualValue[allowedValues]
            : allowedValues && allowedValues.includes(actualValue);
        }

        return typeof actualValue === "object"
          ? actualValue[allowedValues]
          : actualValue === true ||
              (allowedValues && allowedValues.includes(actualValue));
      });
    }

    if (dependOnExact) {
      const list = dependOnExact[0];
      const exactNumber = dependOnExact[1];
      const trueValues = list.filter((el) => !!fields[el.fieldKey].value);
      return trueValues.length >= exactNumber;
    }

    return true;
  };

  const mandatoryFieldsCompleted = Object.entries(fields)
    .filter(
      ([_, { mandatory, dependOn, dependOnAny, dependOnExact }]) =>
        checkIfShouldShow(dependOn, undefined, dependOnAny, dependOnExact) &&
        mandatory
    )
    .every(([_, { value, type }]) => {
      if (typeof value === "string") {
        return value !== "";
      }
      // this is sufficient as the typeof undefined is not boolean
      if (typeof value === "boolean") {
        return true;
      }
      if (type === "multivalueCheckbox") {
        return Object.values(value).some((checkbox) => checkbox === true);
      }
      if (type === "dob") {
        return (
          value.days !== undefined &&
          value.months !== undefined &&
          value.year !== undefined
        );
      }
      return false;
    });

  const validationsPass = Object.entries(fields)
    .filter(([_, { validate }]) => validate)
    .every(([_, { value, validate }]) => validate(value));

  const isSubmittable = mandatoryFieldsCompleted && validationsPass;

  const wipeNotShownFieldsBeforeSubmit = () => {
    const keysToWipe = Object.entries(fields)
      .filter(([_, { dependOn }]) => !checkIfShouldShow(dependOn))
      .map(([key]) => key);

    setField(keysToWipe, "");
  };

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isSubmitting) {
      handleSubmit();
      setSubmitting(false);
    }
  }, [isSubmitting]);

  const submit = () => {
    setSubmitting(true);
    wipeNotShownFieldsBeforeSubmit();
  };

  const renderField = ([
    key,
    {
      title,
      value,
      type = "text",
      dependOn,
      dependOnAny,
      mandatory,
      htmlObj,
      hint,
      checkboxText1,
      checkboxText2,
      validate,
      selectOptions,
      optionsDetailsInputs,
      radioOptions,
      tableDetails,
      display,
      template,
      disabled = false,
      seeAsterix = true,
      style,
      inputWidth,
      dependOnExact,
    },
  ]) => {
    if (checkIfShouldShow(dependOn, display, dependOnAny, dependOnExact)) {
      return (
        <div className="form-group" style={style} key={key}>
          <div>
            {htmlObj ||
              (type !== "singlecheckbox" && title && (
                <label htmlFor={key} className={`mb-hf`}>
                  {title}
                  {mandatory && seeAsterix ? "*" : ""}
                  {hint ? (
                    <OverflowedText
                      forceOverflow
                      overflowedText={hint}
                      styles={{ display: "inline" }}
                    >
                      <HelpIcon className="ml-hf" />
                    </OverflowedText>
                  ) : (
                    ""
                  )}
                </label>
              ))}
          </div>
          {value !== undefined &&
            ["text", "number"].includes(type) &&
            (template ? (
              template(
                <TextFieldGroup
                  inputId={key}
                  classes={`register_field d-inline col-9 col-sm-6 col-lg-5 ${
                    validate && !validate(value) ? styles.hasError : ""
                  }`}
                  type={type}
                  value={value}
                  onChange={(e) => {
                    setField(key, e.target.value);
                  }}
                  inputWidth={inputWidth}
                />
              )
            ) : (
              <TextFieldGroup
                inputId={key}
                classes={`register_field ${
                  validate && !validate(value) ? styles.hasError : ""
                }`}
                type={type}
                value={value}
                isDisabled={disabled}
                inputWidth={inputWidth}
                onChange={(e) => {
                  setField(key, e.target.value);
                }}
              />
            ))}
          {type === "checkbox" && (
            <OptionsButton
              items={[
                {
                  text: `${checkboxText1 || "Yes"}`,
                  key,
                  value: true,
                  isActive: value === true,
                },
                {
                  text: `${checkboxText2 || "No"}`,
                  key,
                  value: false,
                  isActive: value === false,
                },
              ]}
              handleChange={setField}
            />
          )}
          {type === "singlecheckbox" && (
            <Checkbox
              label={title}
              name={title}
              isChecked={value === true}
              onClick={() => setField(key, !value)}
            />
          )}
          {type === "dob" && (
            <div className={`form-group row justify-content-left d-flex`}>
              <DOB
                title={""}
                dayOfBirth={`${value.days}`}
                monthOfBirth={`${value.months}`}
                yearOfBirth={`${value.year}`}
                handleChange={setField}
              />
            </div>
          )}
          {type === "multivalueCheckbox" && (
            <div>
              {selectOptions.map((option) => {
                const nestedField =
                  optionsDetailsInputs && optionsDetailsInputs[option.value];
                return (
                  <div
                    key={option.value}
                    style={{ alignItems: "center", padding: "5px" }}
                    className="row no-gutters"
                  >
                    <Checkbox
                      id={option.value}
                      isChecked={value[option.value]}
                      onClick={(e) => {
                        const newValue = {
                          ...value,
                          [option.value]: e.target.checked,
                        };
                        setField(key, newValue);
                      }}
                      label={option.label}
                    />
                    {nestedField && (
                      <div className="col-5 col-sm-3">
                        {renderField([
                          nestedField,
                          { ...fields[nestedField], display: true },
                        ])}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {type === "radio" && (
            <div>
              {radioOptions.map((option) => (
                <RadioButton
                  key={option}
                  inputId={option}
                  label={option}
                  isChecked={value === option}
                  onClick={() => setField(key, option)}
                />
              ))}
            </div>
          )}
          {type === "textarea" && (
            <TextAreaGroup
              value={value}
              onChange={(e) => {
                setField(key, e.target.value);
              }}
            />
          )}
          {type === "select" && (
            <SelectListGroup
              id={`${formTitle}-${title.replace("", "-")}`}
              value={value}
              onChange={(e) => setField(key, e.target.value)}
              options={selectOptions}
              classes="register_field-dropdown"
            />
          )}
          {type === "table" && (
            <Table
              tableKey={key}
              value={value}
              setField={setField}
              tableDetails={tableDetails}
            />
          )}
        </div>
      );
    }
    return <Fragment key={key} />;
  };

  return (
    <div className="container d-flex flex-column align-items-center justify-content-center pt-2">
      <Header showDescription classes="w-100" description={description}>
        <h2 className="mb-hf">{formTitle}</h2>
      </Header>
      <p className={styles.formCreator__required}>{helper}</p>
      <div className="col-sm-11 col-md-8 col-lg-6 mb-2 section-body p-1 mx-1 mx-sm-0">
        {Object.entries(fields).map(renderField)}
        {customHtml}
        <div className="d-flex justify-content-end mt-1 mt-sm-0 flex-sm-row flex-column-reverse">
          <button
            className="btn btn-outline-dark text-dark hover-text-white col-12 col-sm-3 col-lg-3 mt-1 mt-sm-0"
            onClick={handleCancel}
          >
            Cancel
          </button>
          {isCustomSaveButton ? (
            <button
              className="btn btn-primary col-12 col-sm-3 col-lg-3 ml-sm-1 "
              disabled={!isSubmittable || disableSave}
              onClick={handleSave}
            >
              Save
            </button>
          ) : (
            <button
              className="btn btn-primary col-12 col-sm-3 col-lg-3 ml-sm-1 "
              disabled={!isSubmittable || loading}
              onClick={submit}
            >
              {["royalMail", "driving"].includes(onboardingBranch)
                ? "Submit"
                : "Save"}
              {loading && <Spinner />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

FormCreator.propTypes = {
  formTitle: string,
  description: string,
  fields: any,
  setField: any,
  handleSubmit: func,
  handleCancel: func,
  onboardingBranch: string,
  helper: string,
  customHtml: any,
  handleSave: func,
  isCustomSaveButton: bool,
  disableSave: bool,
  loading: bool,
};

export default FormCreator;
